// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper.js";
import type { FleetspaceAccount } from "../operationsInterfaces/index.js";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers.js";
import * as Parameters from "../models/parameters.js";
import type { CosmosDBManagementClient } from "../cosmosDBManagementClient.js";
import type { SimplePollerLike, OperationState } from "@azure/core-lro";
import { createHttpPoller } from "@azure/core-lro";
import { createLroSpec } from "../lroImpl.js";
import type {
  FleetspaceAccountResource,
  FleetspaceAccountListNextOptionalParams,
  FleetspaceAccountListOptionalParams,
  FleetspaceAccountListResponse,
  FleetspaceAccountGetOptionalParams,
  FleetspaceAccountGetResponse,
  FleetspaceAccountCreateOptionalParams,
  FleetspaceAccountCreateResponse,
  FleetspaceAccountDeleteOptionalParams,
  FleetspaceAccountDeleteResponse,
  FleetspaceAccountListNextResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Class containing FleetspaceAccount operations. */
export class FleetspaceAccountImpl implements FleetspaceAccount {
  private readonly client: CosmosDBManagementClient;

  /**
   * Initialize a new instance of the class FleetspaceAccount class.
   * @param client Reference to the service client
   */
  constructor(client: CosmosDBManagementClient) {
    this.client = client;
  }

  /**
   * Lists all the fleetspaces accounts  under a fleetspace.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param fleetspaceName Cosmos DB fleetspace name. Needs to be unique under a fleet.
   * @param options The options parameters.
   */
  public list(
    resourceGroupName: string,
    fleetName: string,
    fleetspaceName: string,
    options?: FleetspaceAccountListOptionalParams,
  ): PagedAsyncIterableIterator<FleetspaceAccountResource> {
    const iter = this.listPagingAll(resourceGroupName, fleetName, fleetspaceName, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listPagingPage(resourceGroupName, fleetName, fleetspaceName, options, settings);
      },
    };
  }

  private async *listPagingPage(
    resourceGroupName: string,
    fleetName: string,
    fleetspaceName: string,
    options?: FleetspaceAccountListOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<FleetspaceAccountResource[]> {
    let result: FleetspaceAccountListResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._list(resourceGroupName, fleetName, fleetspaceName, options);
      const page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listNext(
        resourceGroupName,
        fleetName,
        fleetspaceName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      const page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listPagingAll(
    resourceGroupName: string,
    fleetName: string,
    fleetspaceName: string,
    options?: FleetspaceAccountListOptionalParams,
  ): AsyncIterableIterator<FleetspaceAccountResource> {
    for await (const page of this.listPagingPage(
      resourceGroupName,
      fleetName,
      fleetspaceName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Lists all the fleetspaces accounts  under a fleetspace.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param fleetspaceName Cosmos DB fleetspace name. Needs to be unique under a fleet.
   * @param options The options parameters.
   */
  private _list(
    resourceGroupName: string,
    fleetName: string,
    fleetspaceName: string,
    options?: FleetspaceAccountListOptionalParams,
  ): Promise<FleetspaceAccountListResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, fleetName, fleetspaceName, options },
      listOperationSpec,
    );
  }

  /**
   * Retrieves the properties of an existing Azure Cosmos DB fleetspace account under a fleetspace
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param fleetspaceName Cosmos DB fleetspace name. Needs to be unique under a fleet.
   * @param fleetspaceAccountName Cosmos DB fleetspace account name.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    fleetName: string,
    fleetspaceName: string,
    fleetspaceAccountName: string,
    options?: FleetspaceAccountGetOptionalParams,
  ): Promise<FleetspaceAccountGetResponse> {
    return this.client.sendOperationRequest(
      {
        resourceGroupName,
        fleetName,
        fleetspaceName,
        fleetspaceAccountName,
        options,
      },
      getOperationSpec,
    );
  }

  /**
   * Creates an Azure Cosmos DB fleetspace account under a fleetspace.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param fleetspaceName Cosmos DB fleetspace name. Needs to be unique under a fleet.
   * @param fleetspaceAccountName Cosmos DB fleetspace account name.
   * @param body The parameters to provide for the current fleetspace account.
   * @param options The options parameters.
   */
  async beginCreate(
    resourceGroupName: string,
    fleetName: string,
    fleetspaceName: string,
    fleetspaceAccountName: string,
    body: FleetspaceAccountResource,
    options?: FleetspaceAccountCreateOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<FleetspaceAccountCreateResponse>,
      FleetspaceAccountCreateResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<FleetspaceAccountCreateResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: {
        resourceGroupName,
        fleetName,
        fleetspaceName,
        fleetspaceAccountName,
        body,
        options,
      },
      spec: createOperationSpec,
    });
    const poller = await createHttpPoller<
      FleetspaceAccountCreateResponse,
      OperationState<FleetspaceAccountCreateResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "azure-async-operation",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Creates an Azure Cosmos DB fleetspace account under a fleetspace.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param fleetspaceName Cosmos DB fleetspace name. Needs to be unique under a fleet.
   * @param fleetspaceAccountName Cosmos DB fleetspace account name.
   * @param body The parameters to provide for the current fleetspace account.
   * @param options The options parameters.
   */
  async beginCreateAndWait(
    resourceGroupName: string,
    fleetName: string,
    fleetspaceName: string,
    fleetspaceAccountName: string,
    body: FleetspaceAccountResource,
    options?: FleetspaceAccountCreateOptionalParams,
  ): Promise<FleetspaceAccountCreateResponse> {
    const poller = await this.beginCreate(
      resourceGroupName,
      fleetName,
      fleetspaceName,
      fleetspaceAccountName,
      body,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Removes an existing Azure Cosmos DB fleetspace account from a fleetspace.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param fleetspaceName Cosmos DB fleetspace name. Needs to be unique under a fleet.
   * @param fleetspaceAccountName Cosmos DB fleetspace account name.
   * @param options The options parameters.
   */
  async beginDelete(
    resourceGroupName: string,
    fleetName: string,
    fleetspaceName: string,
    fleetspaceAccountName: string,
    options?: FleetspaceAccountDeleteOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<FleetspaceAccountDeleteResponse>,
      FleetspaceAccountDeleteResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<FleetspaceAccountDeleteResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: {
        resourceGroupName,
        fleetName,
        fleetspaceName,
        fleetspaceAccountName,
        options,
      },
      spec: deleteOperationSpec,
    });
    const poller = await createHttpPoller<
      FleetspaceAccountDeleteResponse,
      OperationState<FleetspaceAccountDeleteResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "azure-async-operation",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Removes an existing Azure Cosmos DB fleetspace account from a fleetspace.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param fleetspaceName Cosmos DB fleetspace name. Needs to be unique under a fleet.
   * @param fleetspaceAccountName Cosmos DB fleetspace account name.
   * @param options The options parameters.
   */
  async beginDeleteAndWait(
    resourceGroupName: string,
    fleetName: string,
    fleetspaceName: string,
    fleetspaceAccountName: string,
    options?: FleetspaceAccountDeleteOptionalParams,
  ): Promise<FleetspaceAccountDeleteResponse> {
    const poller = await this.beginDelete(
      resourceGroupName,
      fleetName,
      fleetspaceName,
      fleetspaceAccountName,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * ListNext
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param fleetspaceName Cosmos DB fleetspace name. Needs to be unique under a fleet.
   * @param nextLink The nextLink from the previous successful call to the List method.
   * @param options The options parameters.
   */
  private _listNext(
    resourceGroupName: string,
    fleetName: string,
    fleetspaceName: string,
    nextLink: string,
    options?: FleetspaceAccountListNextOptionalParams,
  ): Promise<FleetspaceAccountListNextResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, fleetName, fleetspaceName, nextLink, options },
      listNextOperationSpec,
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const listOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.DocumentDB/fleets/{fleetName}/fleetspaces/{fleetspaceName}/fleetspaceAccounts",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.FleetspaceAccountListResult,
    },
    default: {
      bodyMapper: Mappers.ErrorResponse,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.fleetName,
    Parameters.fleetspaceName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.DocumentDB/fleets/{fleetName}/fleetspaces/{fleetspaceName}/fleetspaceAccounts/{fleetspaceAccountName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.FleetspaceAccountResource,
    },
    default: {
      bodyMapper: Mappers.ErrorResponse,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.fleetName,
    Parameters.fleetspaceName,
    Parameters.fleetspaceAccountName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const createOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.DocumentDB/fleets/{fleetName}/fleetspaces/{fleetspaceName}/fleetspaceAccounts/{fleetspaceAccountName}",
  httpMethod: "PUT",
  responses: {
    200: {
      bodyMapper: Mappers.FleetspaceAccountResource,
    },
    201: {
      bodyMapper: Mappers.FleetspaceAccountResource,
    },
    202: {
      bodyMapper: Mappers.FleetspaceAccountResource,
    },
    204: {
      bodyMapper: Mappers.FleetspaceAccountResource,
    },
    default: {
      bodyMapper: Mappers.ErrorResponse,
    },
  },
  requestBody: Parameters.body7,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.fleetName,
    Parameters.fleetspaceName,
    Parameters.fleetspaceAccountName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const deleteOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.DocumentDB/fleets/{fleetName}/fleetspaces/{fleetspaceName}/fleetspaceAccounts/{fleetspaceAccountName}",
  httpMethod: "DELETE",
  responses: {
    200: {
      headersMapper: Mappers.FleetspaceAccountDeleteHeaders,
    },
    201: {
      headersMapper: Mappers.FleetspaceAccountDeleteHeaders,
    },
    202: {
      headersMapper: Mappers.FleetspaceAccountDeleteHeaders,
    },
    204: {
      headersMapper: Mappers.FleetspaceAccountDeleteHeaders,
    },
    default: {
      bodyMapper: Mappers.ErrorResponse,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.fleetName,
    Parameters.fleetspaceName,
    Parameters.fleetspaceAccountName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.FleetspaceAccountListResult,
    },
    default: {
      bodyMapper: Mappers.ErrorResponse,
    },
  },
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.nextLink,
    Parameters.fleetName,
    Parameters.fleetspaceName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
